import { queryClient } from '@/router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { type SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { API_ENDPOINTS } from '@/lib/constants/api'
import { useDialog } from '@/lib/hooks/use-dialog'
import { updateCaseExtraInfoMutation } from '@/lib/queries/business/v1/case-extra/mutations'
import { getCaseExtraInfoQuery, getCaseExtraInfoQueryKey } from '@/lib/queries/business/v3/case-extra/queries'
import { uscisFormCategoriesQuery } from '@/lib/queries/case/public/trend/processing-time/queries'

import { useFindTargetCase } from '../../../hooks/use-find-target-case'
import { useUpdateCaseInfoDialogStore } from '../../stores/use-update-case-info-dialog-store'

const formSchema = z.object({
  category: z.string(),
  serviceLocation: z.string(),
  receiptDate: z.date(),
})

export const useUpdateProcessingTimeAlertDialog = () => {
  const { isDialogOpen: isOpenUpdateCaseInfoDialog, closeDialog: closeUpdateCaseInfoDialog } =
    useUpdateCaseInfoDialogStore((state) => state)
  const { targetCase } = useFindTargetCase()

  const caseId = targetCase?.case_id
  const caseType = targetCase?.case_type

  const { data: dataUscisFormCategories } = useQuery(uscisFormCategoriesQuery(caseType))
  const { data: dataCaseExtraInfo } = useQuery(getCaseExtraInfoQuery(caseId))
  const { mutateAsync: updateCaseExtraInfo, isPending: isPendingUpdateCaseExtraInfo } =
    useMutation(updateCaseExtraInfoMutation)

  const {
    isDialogOpen: isOpenServiceCenterExplanationDialog,
    openDialog: openServiceCenterExplanationDialog,
    closeDialog: closeServiceCenterExplanationDialog,
  } = useDialog()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
  })

  const { category, serviceLocation, receiptDate } = useWatch({
    control: form.control,
  })
  const serviceCenters = dataUscisFormCategories?.find((item) => item.code === category)?.fieldOffice ?? []
  const isDisabledGetProcessingTimeButton = !category || !serviceLocation || !receiptDate

  const handleChangeDialogOpen = (open: boolean) => {
    if (open) {
      return
    }
    form.reset()
    closeUpdateCaseInfoDialog()
  }

  const handleSubmitForm: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (!caseId) {
      return
    }

    const { category, serviceLocation, receiptDate } = data
    await updateCaseExtraInfo(
      {
        caseId,
        applicationFiledDate: dayjs(receiptDate).format('YYYY-MM-DD'),
        categoryCode: category,
        officeCode: serviceLocation,
      },
      {
        onSuccess: async () => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getCaseExtraInfoQueryKey(caseId),
            }),
            queryClient.invalidateQueries({
              queryKey: [API_ENDPOINTS.business.v4.case.search.index],
            }),
          ])
          closeUpdateCaseInfoDialog()
        },
      }
    )
  }

  useEffect(() => {
    if (dataCaseExtraInfo) {
      const { categoryCode, officeCode, applicationFiledDate } = dataCaseExtraInfo

      form.reset({
        category: categoryCode,
        serviceLocation: officeCode,
        receiptDate: applicationFiledDate ? dayjs(applicationFiledDate).toDate() : undefined,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCaseExtraInfo])

  return {
    form,
    caseType,
    dataUscisFormCategories,
    serviceCenters,
    isOpenUpdateCaseInfoDialog,
    isOpenServiceCenterExplanationDialog,
    isPendingUpdateCaseExtraInfo,
    isDisabledGetProcessingTimeButton,
    openServiceCenterExplanationDialog,
    closeServiceCenterExplanationDialog,
    handleSubmitForm,
    handleChangeDialogOpen,
  }
}
